import { StreamingPlayerUIPageV2 } from '@heardis/stream-contracts';
import { useEffect, useState } from 'react';
import ListIcon from '../../../elements/icons/ListIcon';
import { AnalyticsEventType } from '../../../hooks/analytics/analytics.types';
import useAnalytics from '../../../hooks/analytics/useAnalytics';
import useMediaQuery from '../../../hooks/useMediaQuery';
import { SidePanelStatus, SidePanelTab } from './three-rows-page-layout.types';
import { Logger } from '../../../utils/logger';
import { classNames } from '../../../utils/styles';
import Player from '../../Player/Player';
import Footer from '../Footer/Footer';
import Header from '../Header/Header';
import SidePanel from '../SidePanel/SidePanel';
import classes from './ThreeRowsPageLayout.module.css';
import { SettingsContextAnalyticsStatus, useSettingsContext } from '../../../contexts/SettingsContext';
import ErrorContainer from '../../InfoContainer/ErrorContainer';
import { BackgroundImage } from '../../Image';
import CookieContainer from '../../InfoContainer/CookieContainer';

const logger = new Logger('ThreeRowsPageLayout');
export type ThreeRowsPageLayoutProps = {
  page: StreamingPlayerUIPageV2;
};

function ThreeRowsPageLayout({ page }: ThreeRowsPageLayoutProps): JSX.Element {
  const { trackAnalyticsEvent } = useAnalytics();
  const { error, analytics, setAnalytics } = useSettingsContext();
  const [showInfoContainer, setShowInfoContainer] = useState(false);
  const [sidePanelStatus, setSidePanelStatus] = useState<{ tab: SidePanelTab; isActive: boolean }>({ tab: SidePanelTab.DESCRIPTION, isActive: false });
  const isMobile = !useMediaQuery('(min-width: 1024px)');
  const isLandscape = useMediaQuery('(orientation: landscape)');
  const { sections, typography, background, features, description, logo, title, player } = page;
  const { footer, header, primary, secondary } = sections;
  const { fontFamily, fontSize, fontWeight, color } = typography;
  const { color: backgroundColor, image: backgroundImage } = background;
  const { description: descriptionEnabled, notes: notesEnabled, spotify: spotifyEnabled, tracklist: tracklistEnabled, analytics: analyticsEnabled } = features;
  /** @TOASK I would leave the enabled to the feature props and show something like "no content" instead */
  const sidePanelEnabled = tracklistEnabled || descriptionEnabled;

  useEffect(() => {
    if (!analyticsEnabled) {
      setAnalytics(SettingsContextAnalyticsStatus.DISABLED);
    }
  }, []);

  useEffect(() => {
    if (error) {
      setShowInfoContainer(true);
    } else {
      setShowInfoContainer(false);
    }
  }, [error]);

  /** @todo (ignore for now) refactor showtracklist on mobile to not be in the player context but by component composition */
  const showTab = (tab: SidePanelTab) => {
    setSidePanelStatus({ tab, isActive: true });
  };

  const handleSidePanelsActiveStatus = () => {
    setSidePanelStatus((currentSidePanelStatus) => ({ ...currentSidePanelStatus, isActive: !currentSidePanelStatus.isActive }));
  };

  const handleTracklistButtonClick = () => {
    if (!sidePanelStatus.isActive) {
      showTab(SidePanelTab.TRACKLIST);
    } else {
      handleSidePanelsActiveStatus();
    }
  };

  useEffect(() => {
    const params = new URL(window.location.href).searchParams;
    const infoTab = params.get('tab');
    const infoStatus = params.get('info');

    if (infoStatus === SidePanelStatus.ACTIVE) {
      setSidePanelStatus((currentSidePanelStatus) => ({ ...currentSidePanelStatus, isActive: true }));
    }

    switch (infoTab) {
      case SidePanelTab.DESCRIPTION:
        return setSidePanelStatus((currentSidePanelStatus) => ({ ...currentSidePanelStatus, tab: SidePanelTab.DESCRIPTION }));
      case SidePanelTab.TRACKLIST:
        return setSidePanelStatus((currentSidePanelStatus) => ({ ...currentSidePanelStatus, tab: SidePanelTab.TRACKLIST }));
      default:
        if (descriptionEnabled) return setSidePanelStatus((currentSidePanelStatus) => ({ ...currentSidePanelStatus, tab: SidePanelTab.DESCRIPTION }));
        if (tracklistEnabled) return setSidePanelStatus((currentSidePanelStatus) => ({ ...currentSidePanelStatus, tab: SidePanelTab.TRACKLIST }));
    }
  }, []);

  useEffect(() => {
    if (!sidePanelStatus.isActive) return;

    switch (sidePanelStatus.tab) {
      case SidePanelTab.DESCRIPTION:
        trackAnalyticsEvent({ type: AnalyticsEventType.SHOW_DESCRIPTION });
        break;
      case SidePanelTab.TRACKLIST:
        trackAnalyticsEvent({ type: AnalyticsEventType.SHOW_TRACKLIST });
        break;
      default: logger.debug("Side panel tab doesn't trigger analytics event:", sidePanelStatus.tab);
    }
  }, [sidePanelStatus.isActive, sidePanelStatus.tab]);

  return (
    <div className={classes.outerPageWrapper} style={{ fontFamily, fontSize, fontWeight, color, backgroundColor }}>
      {analytics === SettingsContextAnalyticsStatus.NOT_SET && (
      <div className={classes.cookieOverlay}>
        <div className={classNames(classes.infoContainerWrapper, classes.active)}>
          <CookieContainer
            backgroundColor={secondary.background.color}
            typography={secondary.typography}
          />
        </div>
      </div>
      )}
      <div className={classNames(classes.infoContainerWrapper, showInfoContainer ? classes.active : '')}>
        <ErrorContainer error={error?.code} backgroundColor={secondary.background.color} typography={secondary.typography} />
      </div>
      {sidePanelEnabled && (
        <SidePanel
          descriptionEnabled={descriptionEnabled}
          notesEnabled={notesEnabled}
          spotifyEnabled={spotifyEnabled}
          tracklistEnabled={tracklistEnabled}
          description={description}
          backgroundColor={secondary.background.color}
          backgroundImage={secondary.background.image}
          typography={secondary.typography}
          sidePanelStatus={sidePanelStatus}
          handleSidePanelsActiveStatus={handleSidePanelsActiveStatus}
          showTab={showTab}
        />
      )}
      <div className={classNames(classes.pageWrapper, sidePanelStatus.isActive ? classes.activeInfo : '')}>
        <Header
          backgroundColor={header.background.color}
          backgroundImage={header.background.image}
          typography={header.typography}
          logo={logo}
          title={title}
          descriptionEnabled={descriptionEnabled}
          showDescription={() => showTab(SidePanelTab.DESCRIPTION)}
        />
        <main
          className={classes.main}
          style={{
            backgroundColor: primary.background.color,
            fontSize: primary.typography.fontSize,
            fontFamily: primary.typography.fontFamily,
            fontWeight: primary.typography.fontWeight,
            color: primary.typography.color,
          }}
        >
          <div className={classNames(classes.playerWrapper, sidePanelStatus.isActive ? classes.activeInfo : '', player.artwork.enabled ? classes.artworkEnabled : '')}>
            <Player
              typography={player.typography}
              elementsColor={player.elementsColor}
              backgroundColor={player.background.color}
              backgroundImage={player.background.image}
              artwork={player.artwork}
              spotifyEnabled={spotifyEnabled}
              vertical={(isMobile && !isLandscape) || (sidePanelStatus.isActive && !isMobile)}
                // @TOASK I temporarely moved it here like this. How would you expect it?
              controlFeatureRight={tracklistEnabled && (
              <button
                type="button"
                onClick={handleTracklistButtonClick}
              >
                <ListIcon fill={player.elementsColor} />
              </button>
              )}
            />
          </div>
          {primary.background.image.url && (
            <BackgroundImage
              url={primary.background.image.url}
              alt={primary.background.image.alt}
              blur={primary.background.image.blur}
              position={primary.background.image.position}
              fit={primary.background.image.fit}
              zIndex={-10}
            />
          )}
        </main>
        <Footer
          typography={footer.typography}
          backgroundColor={footer.background.color}
          backgroundImage={footer.background.image}
          analyticsEnabled={analyticsEnabled}
          showCookieBanner={() => setAnalytics(SettingsContextAnalyticsStatus.NOT_SET)}
        />
        {backgroundImage.url && (
          <BackgroundImage
            url={backgroundImage.url}
            alt={backgroundImage.alt}
            blur={backgroundImage.blur}
            position={backgroundImage.position}
            fit={backgroundImage.fit}
            zIndex={-20}
          />
        )}
      </div>
    </div>
  );
}

export default ThreeRowsPageLayout;
